import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import _ from 'lodash'
import { toast } from 'react-toastify'

import { CircularProgress, TextField, Typography } from '@material-ui/core'

import { Button, Checkbox, DialogConfirm, PermissionSelect } from 'ui/components'
import DataTable from 'ui/components/ContainerCommonPanels/DataTable'
import ButtonUserCreate from 'ui/components/ButtonUserCreate'
import ModalSelectOrganization from 'ui/components/ModalSelectOrganization'

import { getUsers } from 'services/apiMotorBgc'
import { patchAdminUser } from 'services/apiMotorBgcAdmin'
import reportError from 'utils/errorReporter'
import { isPermissionAdminOrGreater } from 'utils/validators'

import { columnsUsers } from './configDataTable'

import styles from './styles'

export default function () {
  const classes = styles()

  const apiKey = useSelector((state) => state.auth.api_key)
  const userId = useSelector((state) => state.auth.sub)
  const accessToken = useSelector(state => state.auth.access_token)
  const userPermission = useSelector(state => state.user.permission)
  const userOrgId = useSelector((state) => state.user.org_id)
  const myUserEmail = useSelector((state) => state.user.email)

  const orgDefault = { org_id: userOrgId }

  const [loading, setLoading] = useState(false)
  const [searchEmail, setSearchEmail] = useState('')
  const [users, setUsers] = useState([])
  const [pageData, setPageData] = useState({ current: 1, totalItems: 0, totalPages: 0, perPage: 0 })
  const [orgSelected, setOrgSelected] = useState(orgDefault)

  const [userToEdit, setUserToEdit] = useState(null)
  const [dialogUpdateUserOpen, setDialogUpdateUserOpen] = useState(false)
  const [updateUserLoading, setUpdateUserLoading] = useState(false)

  const patchUserData = async (options = {}) => {
    setUpdateUserLoading(true)

    try {
      await patchAdminUser(
        apiKey,
        userId,
        accessToken,
        {
          userEmailToUpdate: options.userEmailToUpdate,
          userData: options.data,
        },
      )

      toast.success('O usuário foi alterado')
    } catch (err) {
      reportError(err, 'Ocorreu um erro ao alterar o usuário')
    }

    setUpdateUserLoading(false)
  }

  const fetchUsers = async (orgId, options = {}) => {
    setLoading(true)

    let res = null
    try {
      res = await getUsers(
        apiKey,
        userId,
        orgId,
        {
          email: options.email,
          page: _.has(options, 'page') ? options.page : pageData.current,
        },
      )
    } catch (err) {
      reportError(err, 'Erro ao obter usuários')
      setLoading(false)
      return
    }

    const current = parseInt(res.headers['x-page'], 10)
    const totalItems = parseInt(res.headers['x-total'], 10)
    const perPage = parseInt(res.headers['x-per-page'], 10)
    const totalPages =  Math.ceil(totalItems / perPage)
    setPageData({ current: current, totalPages: totalPages, totalItems: totalItems, perPage: perPage })
    setUsers(res.data.users)
    setLoading(false)
  }

  useEffect(() => {
    fetchUsers(orgSelected.org_id, { page: 1 })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgSelected])

  const handleSelectOrganization = (org) => {
    if (_.isEmpty(org)) {
      setOrgSelected(orgDefault)
      return
    }
    setOrgSelected(org)
  }

  const handleUserCreate = () => {
    setSearchEmail('')
    fetchUsers(orgSelected.org_id, { page: 1 })
  }

  const handleSearchEmail = () => {
    fetchUsers(orgSelected.org_id, { email: searchEmail, page: 1 })
  }

  const handleChangePage = (number) => {
    fetchUsers(orgSelected.org_id, { email: searchEmail, page: number + 1 })
  }

  const handleOpenDialogUpdateUser = (event, user) => {
    event.stopPropagation()

    setUserToEdit(user)
    setDialogUpdateUserOpen(true)
  }

  const handleUpdateUserCancel  = async (event) => {
    event.stopPropagation()
    setDialogUpdateUserOpen(false)
    setUserToEdit(null)
  }

  const handleUpdateUserConfirm  = async (event, user) => {
    event.stopPropagation()

    await patchUserData({
      userEmailToUpdate: user.email,
      data: {
        actived: user.actived,
        permission: user.permission,
      },
    })
    fetchUsers(orgSelected.org_id)

    setDialogUpdateUserOpen(false)
    setUserToEdit(null)
  }

  const handleUpdateUserActived = (actived) => {
    setUserToEdit({ ...userToEdit, actived: actived })
  }

  const handleUpdateUserPermission = (event) => {
    setUserToEdit({ ...userToEdit, permission: event.target.value })
  }

  const renderButtonUpdateUser = (user) => {
    if (myUserEmail === user.email) return null

    return (
      <Button
        variant="outlined"
        size="small"
        onClick={(e) => handleOpenDialogUpdateUser(e, user)}
      >
        Editar
      </Button>
    )
  }

  const renderUserActions = (user) => {
    if (!isPermissionAdminOrGreater(userPermission)) return null

    return (
      <div>
        {renderButtonUpdateUser(user)}
      </div>
    )
  }

  const usersWithAction = () => {
    return _.map(users, (user) => {
      const newUser = _.cloneDeep(user)
      newUser.action = renderUserActions(user)

      return newUser
    })
  }

  const renderUsers = () => {
    if (loading) return <CircularProgress />

    return (
      <DataTable
        columns={columnsUsers}
        data={usersWithAction()}
        options={{
          pagination: true,
          serverSide: true,
          rowsPerPage: pageData.perPage,
          count: pageData.totalItems,
          page: pageData.current - 1,
          sort: false,
          onChangePage: handleChangePage,
        }}
      />
    )
  }

  const renderUpdateUserContent = () => {
    return (
      <div className={classes.updateUserContainer}>
        <Typography>{`Usuário: ${userToEdit.email}`}</Typography>

        <Checkbox
          label="Ativado?"
          value={userToEdit.actived}
          onClick={handleUpdateUserActived}
        />

        <PermissionSelect
          value={userToEdit.permission}
          orgName={orgSelected.name}
          onChange={handleUpdateUserPermission}
        />
      </div>
    )
  }

  const renderDialogUpdateUser = () => {
    if (_.isEmpty(userToEdit)) return null

    return (
      <DialogConfirm
        open={dialogUpdateUserOpen}
        onConfirm={(e) => handleUpdateUserConfirm(e, userToEdit)}
        onCancel={(e) => handleUpdateUserCancel(e)}
        title={`Editar`}
        content={renderUpdateUserContent()}
        btnConfirmText="Salvar"
        btnConfirmDisabled={updateUserLoading}
      />
    )
  }

  const renderModalSelectOrganization = () => {
    return (
      <div className={classes.buttonsSelectOrg}>
        <ModalSelectOrganization onSelect={handleSelectOrganization} />
      </div>
    )
  }

  const renderButtonUserCreate = () => {
    return (
      <div className={classes.buttonsUserCreate}>
        <ButtonUserCreate orgSelected={orgSelected} onCreate={handleUserCreate} />
      </div>
    )
  }

  const renderFilterEmail = () => {
    return (
      <div className={classes.buttonFilterEmail}>
        <TextField label="Email" value={searchEmail} onChange={(e) => setSearchEmail(e.target.value)} />
        <Button variant="text" onClick={handleSearchEmail}>Filtrar</Button>
      </div>
    )
  }

  return (
    <div>
      <div className={classes.buttonsContainer}>
        {renderModalSelectOrganization()}
        {renderButtonUserCreate()}
      </div>
      {renderFilterEmail()}
      {renderUsers()}

      {renderDialogUpdateUser()}
    </div>
  )
}
